import { Button } from '@/components/ui-kit/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui-kit/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import type { DocumentStatus } from '@/modules/org/contracts/constants/mock-contract-documents';
import { useResolvedDefaultLetterhead } from '@/modules/org/org-letterheads/hooks/use-resolved-default-letterhead';
import { extractSignatories } from '@/modules/templates/components/review/utils';
import {
  generatePdfForDownload,
  generatePdfWithMetadata,
} from '@/modules/templates/services/pdf.service';
import { signatureRollout } from '@/modules/templates/services/signature.service';
import { sendDocxForSignature } from '@/modules/templates/components/docx-generate/docx-review/docx-signature-send';
import {
  buildFieldsFromSignatories,
  defaultFieldPair,
} from '@/modules/templates/components/docx-generate/docx-review/signature-fields';
import type { LegalDocument } from '@/schemas/schema';
import {
  Archive,
  ChevronDown,
  Download,
  History,
  PenLine,
  Printer,
  Signature,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useUpdateOrgContractDocument } from '@/graphql';
import { parseSignatories } from '../types/contract-document.types';
import { isFixedDocxContract } from '../utils';
import { getContractPdfBytes } from '@/modules/templates/services/docx-convert.service';
import { detectSignatureLocations, type AnchorInput } from '@/lib/pdf/detect-signature-anchors';
import ContractRenameDialog from './contract-rename-dialog';
import ContractTimelineDialog from './contract-timeline-dialog';
import { useAuthStore } from '@/state/store/auth';

interface DocumentRowActionsProps {
  status: DocumentStatus;
  onArchive?: () => void;
  onMoveToTrash?: () => void;
  onResume?: () => void;
  isLoading?: boolean;
  slug: string;
  documentId: string;
  currentTitle: string;
  templateTitle?: string;
  documentRaw?: string;
  formDataRaw?: string;
  signatoriesRaw?: string;
  sigDocumentUrl?: string;
  sigDocumentId?: string;
}

const allStatuses = [
  'draft',
  'generation_completed',
  'preparation_success',
  'rollout_success',
  'signatory_signed_success',
  'signatory_signed_failed',
  'document_completed',
  'document_cancelled',
  'document_declined',
  'preparation_failed',
  'rollout_failed',
  'archived',
];

const statusBasedMenuItems = [
  {
    labelKey: 'ORGCONTRACTS_ACTION_PREPARE_ESIGN',
    icon: Signature,
    hideInDraft: true,
    showInStatus: ['draft', 'generation_completed'],
  },
  { labelKey: 'ORGCONTRACTS_ACTION_RENAME', icon: PenLine, showInStatus: allStatuses },
  {
    labelKey: 'ORGCONTRACTS_ACTION_DOWNLOAD',
    icon: Download,
    hideInDraft: true,
    showInStatus: [
      'generation_completed',
      'preparation_success',
      'rollout_success',
      'signatory_signed_success',
      'document_completed',
    ],
  },
  // { labelKey: 'ORGCONTRACTS_ACTION_DUPLICATE', icon: Copy },
  // { labelKey: 'ORGCONTRACTS_ACTION_ACCESS', icon: KeyRound },
  {
    labelKey: 'ORGCONTRACTS_ACTION_PRINT',
    icon: Printer,
    hideInDraft: true,
    showInStatus: [
      'generation_completed',
      'preparation_success',
      'rollout_success',
      'signatory_signed_success',
      'document_completed',
    ],
  },
  {
    labelKey: 'ORGCONTRACTS_ACTION_ANALYZE_WITH_AI',
    icon: Sparkles,
    showInStatus: [
      'generation_completed',
      'preparation_success',
      'rollout_success',
      'signatory_signed_success',
      'document_completed',
    ],
  },
  { labelKey: 'ORGCONTRACTS_ACTION_TIMELINE', icon: History, showInStatus: allStatuses },
  { labelKey: 'ORGCONTRACTS_ACTION_ARCHIVE', icon: Archive, showInStatus: allStatuses },
  {
    labelKey: 'ORGCONTRACTS_ACTION_MOVE_TO_TRASH',
    icon: Trash2,
    destructive: true,
    showInStatus: allStatuses,
  },
];

const DPI_SCALE = 96 / 72;

const DocumentRowActions = ({
  status,
  slug,
  onArchive,
  onMoveToTrash,
  onResume,
  documentId,
  currentTitle,
  templateTitle,
  documentRaw,
  formDataRaw,
  signatoriesRaw,
  sigDocumentUrl,
  isLoading = false,
}: DocumentRowActionsProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const updateMutation = useUpdateOrgContractDocument();
  const { resolvedLetterhead } = useResolvedDefaultLetterhead();
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [isTimelineOpen, setIsTimelineOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPreparingEsign, setIsPreparingEsign] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isSendingDocx, setIsSendingDocx] = useState(false);
  const activeOrgId = useAuthStore((state) => state.orgId);
  // Fixed-DOCX contracts store a PDF reference (not a LegalDocument), so their
  // actions branch: view opens the DOCX review viewer, download/print use the
  // stored PDF url, send-for-sign reuses the already-detected signature boxes,
  // and there is no editable-document / AI-analyze path.
  const fixedDocx = isFixedDocxContract(documentRaw);
  const isDraft = status === 'draft';
  const isEditable = status === 'draft' && !fixedDocx;
  const menuItems = statusBasedMenuItems.filter((item) => !(isDraft && item.hideInDraft));
  const isBusy = isLoading || isDownloading || isPreparingEsign || isPrinting || isSendingDocx;

  const parseDocumentAndFormData = (): {
    document: LegalDocument;
    formData: Record<string, unknown>;
  } | null => {
    if (!documentRaw || !formDataRaw) return null;
    try {
      const parsedDocument = JSON.parse(documentRaw) as LegalDocument;
      const formData = JSON.parse(formDataRaw) as Record<string, unknown>;
      const document: LegalDocument = {
        ...parsedDocument,
        letterhead: parsedDocument.letterhead || resolvedLetterhead,
      };
      return { document, formData };
    } catch (error) {
      console.error('Failed to parse document/formData JSON', error);
      return null;
    }
  };

  const handleRename = async (newTitle: string) => {
    await updateMutation.mutateAsync({
      ItemId: documentId,
      Name: newTitle,
      TemplateTitle: newTitle,
    });
    toast({
      variant: 'success',
      title: t('ORGCONTRACTS_TOAST_RENAME_SUCCESS'),
    });
  };

  /**
   * This contract's PDF as a Blob, or null when there is none to serve and the
   * caller should regenerate from its `LegalDocument` instead.
   *
   * Order: the finalized signed PDF, else a PDF derived on demand from the
   * stored .docx. Deriving matters — a fixed-DOCX contract stores no PDF
   * precisely because one would go stale the moment the document is edited.
   * `Document.url` remains only as a fallback for records generated before that
   * changed.
   */
  const resolveContractPdfBlob = async (): Promise<Blob | null> => {
    const fetchAsBlob = async (url: string) => {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Failed to fetch PDF: ${response.status}`);
      return response.blob();
    };

    if (sigDocumentUrl) return fetchAsBlob(sigDocumentUrl);
    if (fixedDocx?.docxUrl) {
      const bytes = await getContractPdfBytes({
        docxUrl: fixedDocx.docxUrl,
        docxFileId: fixedDocx.docxFileId,
        fileName: `${slug}.docx`,
      });
      return new Blob([bytes as BlobPart], { type: 'application/pdf' });
    }
    if (fixedDocx?.url) return fetchAsBlob(fixedDocx.url);
    return null;
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const blob = await resolveContractPdfBlob();
      if (blob) {
        // Going through a Blob keeps the filename predictable regardless of the
        // storage URL's Content-Disposition header.
        const objectUrl = window.URL.createObjectURL(blob);
        const link = window.document.createElement('a');
        link.href = objectUrl;
        link.download = sigDocumentUrl
          ? `${slug}-signed.pdf`
          : (fixedDocx?.fileName ?? `${slug}.pdf`);
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
      } else {
        const parsed = parseDocumentAndFormData();
        if (!parsed) {
          toast({
            variant: 'destructive',
            title: t('ORGCONTRACTS_TOAST_DOWNLOAD_ERROR'),
            description: t('ORGCONTRACTS_TOAST_ESIGN_MISSING_DATA'),
          });
          return;
        }
        await generatePdfForDownload(parsed.document, parsed.formData, slug);
      }
      toast({
        variant: 'success',
        title: t('ORGCONTRACTS_TOAST_DOWNLOAD_SUCCESS'),
      });
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast({
        variant: 'destructive',
        title: t('ORGCONTRACTS_TOAST_DOWNLOAD_ERROR'),
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handlePrint = async () => {
    setIsPrinting(true);
    try {
      let url: string;
      // A Blob (rather than the remote URL) so the iframe can call print() on a
      // same-origin document — cross-origin iframes block contentWindow.print.
      const blob = await resolveContractPdfBlob();
      if (blob) {
        url = URL.createObjectURL(blob);
      } else {
        const parsed = parseDocumentAndFormData();
        if (!parsed) {
          toast({
            variant: 'destructive',
            title: t('ORGCONTRACTS_TOAST_PRINT_ERROR'),
            description: t('ORGCONTRACTS_TOAST_ESIGN_MISSING_DATA'),
          });
          setIsPrinting(false);
          return;
        }
        const result = await generatePdfWithMetadata(parsed.document, parsed.formData);
        url = URL.createObjectURL(result.pdfBlob);
      }

      const iframe = window.document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.setAttribute('aria-hidden', 'true');

      const cleanup = () => {
        URL.revokeObjectURL(url);
        iframe.remove();
      };

      iframe.onload = () => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.addEventListener('afterprint', cleanup, { once: true });
          iframe.contentWindow?.print();
        } catch (err) {
          console.error('Failed to trigger print:', err);
          cleanup();
          toast({
            variant: 'destructive',
            title: t('ORGCONTRACTS_TOAST_PRINT_ERROR'),
          });
        }
      };

      iframe.src = url;
      window.document.body.appendChild(iframe);
    } catch (error) {
      console.error('Error preparing print:', error);
      toast({
        variant: 'destructive',
        title: t('ORGCONTRACTS_TOAST_PRINT_ERROR'),
      });
    } finally {
      setIsPrinting(false);
    }
  };

  const handlePrepareEsign = async () => {
    const parsed = parseDocumentAndFormData();
    if (!parsed) {
      toast({
        variant: 'destructive',
        title: t('ORGCONTRACTS_TOAST_ESIGN_ERROR'),
        description: t('ORGCONTRACTS_TOAST_ESIGN_MISSING_DATA'),
      });
      return;
    }

    setIsPreparingEsign(true);
    try {
      const { document, formData } = parsed;

      const reviewSignatories = extractSignatories(document, formData, {
        defaultRole: t('GENERATEREVIEW_DEFAULTS_SIGNATORYROLE'),
        companyName: t('GENERATEREVIEW_DEFAULTS_COMPANYNAME'),
        authorizedRepresentativeRole: t('GENERATEREVIEW_DEFAULTS_AUTHORIZEDREPRESENTATIVE'),
        employeeRole: t('GENERATEREVIEW_DEFAULTS_EMPLOYEEROLE'),
      });

      if (reviewSignatories.length === 0) {
        toast({
          variant: 'destructive',
          title: t('ORGCONTRACTS_TOAST_ESIGN_ERROR'),
          description: t('ORGCONTRACTS_TOAST_ESIGN_NO_SIGNATORIES'),
        });
        return;
      }

      const pdfResult = await generatePdfWithMetadata(document, formData);
      const numPages = pdfResult.metadata.pages;

      const signatoriesForAPI = reviewSignatories.map((sig, idx) => {
        const docSignatory = document.signatories?.[idx];
        return {
          party: docSignatory?.party || sig.role || `signatory_${idx}`,
          name: sig.name,
          email: sig.email,
          role: sig.role,
        };
      });

      const signatureFieldsForAPI = pdfResult.signatureFields.map((field) => {
        const signatoryIndex = field.signatoryIndex ?? (field.party === 'employer' ? 0 : 1);
        const fieldType: 'signature' | 'date' = field.type === 'date' ? 'date' : 'signature';
        return {
          id: field.id,
          type: fieldType,
          signatoryIndex,
          pageNumber: field.pageNumber,
          x: Math.round(field.x * DPI_SCALE),
          y: Math.round(field.y * DPI_SCALE),
          width: Math.round(field.width * DPI_SCALE),
          height: Math.round(field.height * DPI_SCALE),
          label: field.label || (fieldType === 'signature' ? 'Signature' : 'Date'),
        };
      });

      await signatureRollout({
        pdfBase64: pdfResult.pdfBase64,
        document,
        formData,
        signatories: signatoriesForAPI,
        signatureFields: signatureFieldsForAPI,
        templateSlug: slug,
        templateTitle: templateTitle || document.metadata?.title || currentTitle,
        numPages,
        draftId: documentId,
      });

      toast({
        variant: 'success',
        title: t('ORGCONTRACTS_TOAST_ESIGN_SUCCESS'),
      });
    } catch (error) {
      console.error('Error preparing for e-signature:', error);
      toast({
        variant: 'destructive',
        title: t('ORGCONTRACTS_TOAST_ESIGN_ERROR'),
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setIsPreparingEsign(false);
    }
  };

  /**
   * Send a fixed-DOCX contract for signature straight from the row, without the
   * review detour.
   *
   * Signature boxes are resolved here rather than read from the record:
   * generation no longer pre-detects them (the result was always discarded,
   * since editing reflows the document), so we convert the current .docx and
   * locate each anchor on that PDF — the same thing the review page does.
   */
  const handleSendDocxFromRow = async () => {
    const docxUrl = fixedDocx?.docxUrl;
    const legacyPdfUrl = fixedDocx?.url;
    if (!docxUrl && !legacyPdfUrl) return;
    const signatories = parseSignatories(signatoriesRaw);
    if (signatories.length === 0) {
      toast({
        variant: 'destructive',
        title: t('ORGCONTRACTS_TOAST_ESIGN_ERROR'),
        description: t('ORGCONTRACTS_TOAST_ESIGN_PLACE_FIELDS'),
      });
      return;
    }

    setIsSendingDocx(true);
    try {
      const pdfBytes = docxUrl
        ? await getContractPdfBytes({
            docxUrl,
            docxFileId: fixedDocx?.docxFileId,
            fileName: `${slug}.docx`,
          })
        : new Uint8Array(await (await fetch(legacyPdfUrl as string)).arrayBuffer());

      const anchors: AnchorInput[] = signatories.flatMap((s) =>
        s.signatureAnchor?.originalText
          ? [
              {
                signatoryKey: s.id,
                originalText: s.signatureAnchor.originalText,
                contextBefore: s.signatureAnchor.contextBefore,
                contextAfter: s.signatureAnchor.contextAfter,
                paragraphIndex: s.signatureAnchor.paragraphIndex,
              },
            ]
          : []
      );
      const locations = anchors.length ? await detectSignatureLocations(pdfBytes, anchors) : {};
      const resolved = signatories.map((s) => ({
        ...s,
        signatureLocation: locations[s.id] ?? s.signatureLocation ?? null,
      }));

      // Every signatory must end up with a box; anchors that couldn't be located
      // fall back to a default placement rather than blocking the send.
      const fields = buildFieldsFromSignatories(resolved);
      const placed = new Set(
        fields.filter((f) => f.type === 'signature').map((f) => f.signatoryIndex)
      );
      resolved.forEach((_, i) => {
        if (!placed.has(i)) {
          fields.push(...defaultFieldPair(i, 1, { widthPt: 612, heightPt: 792 }));
        }
      });

      // No PDF is rendered here, so page count is approximated from the placed
      // boxes — rollout only needs the pages that actually carry fields.
      const numPages = Math.max(1, ...fields.map((f) => f.pageNumber || 1));
      await sendDocxForSignature({
        pdfBytes,
        documentId,
        templateSlug: slug,
        templateTitle: templateTitle || currentTitle,
        numPages,
        signatureFields: fields,
        signatories: signatories.map((s) => ({
          name: s.name,
          email: s.email,
          role: s.title ?? '',
          order: s.order ?? null,
        })),
      });
      toast({
        variant: 'success',
        title: t('ORGCONTRACTS_TOAST_ESIGN_SUCCESS'),
      });
      navigate(`/templates/${slug}/review/success?document=${encodeURIComponent(documentId)}`);
    } catch (error) {
      console.error('Error sending DOCX for signature:', error);
      toast({
        variant: 'destructive',
        title: t('ORGCONTRACTS_TOAST_ESIGN_ERROR'),
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setIsSendingDocx(false);
    }
  };

  return (
    <div className="inline-flex overflow-hidden rounded-md border border-input bg-background">
      {isEditable ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-9 rounded-none border-0 px-5 text-[hsl(var(--fg))]"
          onClick={onResume}
          disabled={isBusy}
        >
          {t('ORGCONTRACTS_ACTION_EDIT')}
        </Button>
      ) : (
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="h-9 rounded-none border-0 px-5 text-[hsl(var(--fg))]"
          disabled={isBusy}
        >
          <Link
            to={`/templates/${slug}/review?document=${documentId}`}
          >
            {t('ORGCONTRACTS_ACTION_VIEW')}
          </Link>
        </Button>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-9 rounded-none border-l border-input px-2"
            disabled={isBusy}
          >
            <ChevronDown className="size-4" />
            <span className="sr-only">{t('ORGCONTRACTS_ACTION_OPENACTIONS')}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isArchiveItem = item.labelKey === 'ORGCONTRACTS_ACTION_ARCHIVE';
            const isMoveToTrashItem = item.labelKey === 'ORGCONTRACTS_ACTION_MOVE_TO_TRASH';
            const isRenameItem = item.labelKey === 'ORGCONTRACTS_ACTION_RENAME';
            const isDownloadItem = item.labelKey === 'ORGCONTRACTS_ACTION_DOWNLOAD';
            const isPrepareEsignItem = item.labelKey === 'ORGCONTRACTS_ACTION_PREPARE_ESIGN';
            const isPrintItem = item.labelKey === 'ORGCONTRACTS_ACTION_PRINT';
            const isTimelineItem = item.labelKey === 'ORGCONTRACTS_ACTION_TIMELINE';
            const isAnalyzeItem = item.labelKey === 'ORGCONTRACTS_ACTION_ANALYZE_WITH_AI';

            if (!item.showInStatus.includes(status)) {
              return null;
            }

            return (
              <DropdownMenuItem
                key={item.labelKey}
                variant={item.destructive ? 'destructive' : 'default'}
                onSelect={(event) => {
                  if (isBusy) {
                    event.preventDefault();
                    return;
                  }

                  if (isArchiveItem && onArchive) {
                    onArchive();
                  } else if (isMoveToTrashItem && onMoveToTrash) {
                    onMoveToTrash();
                  } else if (isRenameItem) {
                    setIsRenameOpen(true);
                  } else if (isTimelineItem) {
                    setIsTimelineOpen(true);
                  } else if (isDownloadItem) {
                    void handleDownload();
                  } else if (isPrepareEsignItem) {
                    void (fixedDocx ? handleSendDocxFromRow() : handlePrepareEsign());
                  } else if (isPrintItem) {
                    void handlePrint();
                  } else if (isAnalyzeItem) {
                    navigate(`/org/${activeOrgId}/contract-checker/new?contractId=${documentId}`);
                  } else {
                    event.preventDefault();
                  }
                }}
                disabled={isBusy}
              >
                <Icon className="size-4" />
                {t(item.labelKey)}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      <ContractRenameDialog
        open={isRenameOpen}
        onOpenChange={setIsRenameOpen}
        currentTitle={currentTitle}
        onRename={handleRename}
      />

      <ContractTimelineDialog
        open={isTimelineOpen}
        onOpenChange={setIsTimelineOpen}
        documentId={documentId}
        documentTitle={currentTitle}
      />
    </div>
  );
};

export default DocumentRowActions;
